import {Contact, Mail, Phone} from 'lucide-react';
import {useTranslation} from 'react-i18next';

import InfoState from '@/components/InfoState';
import {Avatar, AvatarFallback} from '@/components/ui/avatar';
import type {DialogProps} from '@/components/ui/dialog';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogSection,
  DialogTitle,
} from '@/components/ui/dialog';
import {IconButton} from '@/components/ui/icon-button';
import {
  Item,
  ItemActions,
  ItemContent,
  ItemDescription,
  ItemGroup,
  ItemMedia,
  ItemTitle,
} from '@/components/ui/item';
import {TooltipWrapper} from '@/components/ui/tooltip';
import {graphqlClient} from '@/services/graphqlClient';
import {useGetResidentQuery} from '@/services/graphqlHooks';

import ResidentInput from './ResidentInput';

export type ResidentContactsDialogProps = DialogProps & {
  residentId: string;
  onResidentChange: (residentId: string) => void;
};

function ResidentContactsDialog({
  residentId,
  onResidentChange,
  ...props
}: ResidentContactsDialogProps) {
  const {t} = useTranslation();
  const {data, isLoading} = useGetResidentQuery(
    graphqlClient,
    {id: residentId},
    {enabled: !!residentId}
  );
  const contacts = data?.getResidentById.contactPersons ?? [];

  return (
    <Dialog {...props}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('Resident contacts')}</DialogTitle>
        </DialogHeader>
        <DialogSection className="flex-col gap-4">
          <ResidentInput value={residentId} onChange={onResidentChange} />

          {!residentId && (
            <div className="flex h-64 items-center justify-center">
              <InfoState
                icon={<Contact size={40} className="text-mint-500" />}
                title={t('Select a resident')}
                description={t('Choose a resident to view their contacts')}
              />
            </div>
          )}
          {residentId && !isLoading && contacts.length === 0 && (
            <div className="flex h-64 items-center justify-center">
              <InfoState
                icon={<Contact size={40} className="text-mint-500" />}
                title={t('No contacts found')}
                description={t('This resident has no contacts added yet')}
              />
            </div>
          )}

          {contacts.length > 0 && (
            <ItemGroup>
              {contacts.map(contact => {
                const fullName = [
                  contact.name.firstName,
                  contact.name.middleName,
                  contact.name.lastName,
                ]
                  .filter(Boolean)
                  .join(' ');

                return (
                  <Item
                    key={contact.id ?? `${fullName}-${contact.phoneNumber}`}
                    variant="muted"
                    className="flex flex-wrap items-center gap-3"
                  >
                    <ItemMedia>
                      <Avatar color="primary">
                        <AvatarFallback className="text-primary-800">
                          {contact.name.firstName[0]}
                          {contact.name.lastName[0]}
                        </AvatarFallback>
                      </Avatar>
                    </ItemMedia>
                    <ItemContent>
                      <ItemTitle>{fullName}</ItemTitle>
                      {contact.phoneNumber && (
                        <ItemDescription>{contact.phoneNumber}</ItemDescription>
                      )}
                      {contact.email && (
                        <ItemDescription>{contact.email}</ItemDescription>
                      )}
                    </ItemContent>
                    <ItemActions>
                      {contact.phoneNumber && (
                        <TooltipWrapper
                          title={t('Call {{name}}', {name: fullName})}
                        >
                          <IconButton asChild variant="secondary">
                            <a
                              href={`tel:${contact.phoneNumber}`}
                              aria-label={t('Call {{name}}', {
                                name: fullName,
                              })}
                            >
                              <Phone />
                            </a>
                          </IconButton>
                        </TooltipWrapper>
                      )}
                      {contact.email && (
                        <TooltipWrapper
                          title={t('Email {{name}}', {name: fullName})}
                        >
                          <IconButton asChild variant="secondary">
                            <a
                              href={`mailto:${contact.email}`}
                              aria-label={t('Email {{name}}', {
                                name: fullName,
                              })}
                            >
                              <Mail />
                            </a>
                          </IconButton>
                        </TooltipWrapper>
                      )}
                    </ItemActions>
                  </Item>
                );
              })}
            </ItemGroup>
          )}
        </DialogSection>
      </DialogContent>
    </Dialog>
  );
}

export default ResidentContactsDialog;
